import { useDispatch, useSelector } from 'react-redux';
import { ConnectedRewardsIcon } from '../../uielements';
import { OrderNowCTA } from '../orderNowCTA';
import { ScanCode } from '../scan-code';
import LoyaltyAccessor from '@/components/account/loyaltyAccessor';
import { DonateToSocialCause } from '@/components/account/rewards/donate-to-social-cause/donate-to-social-cause';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { Modal } from '@/components/uielements/modal';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { Render } from '@/components/uielements/render';
import {
  StyledBorderBottom,
  StyledButtonContainer,
  StyledCashButtonContainer,
  StyledNoOffers,
  StyledReward,
  StyledRewardDescription,
  StyledRewardsFlexContainer,
  StyledRewardsHeader,
  StyledRewardsHeaderContainer,
  StyledRewardTopContent,
} from '@/components/uielements/reward/styles';
import {
  LOYALTY_ACCESSOR_TYPES,
  LOYALTY_FEATURES,
  PROGRAM_TYPES,
  REWARD_TYPE,
} from '@/constants/loyalty';
import meActions from '@/redux/me/actions';
import { meSliceSelector } from '@/redux/me/reducer';
import { organizationSliceSelector } from '@/redux/organization/reducer';
import { dateFormatter } from '@/utils/loyalty';

export const MyOffers = () => {
  const { myOffers } = useSelector(meSliceSelector);
  const organization = useSelector(organizationSliceSelector);
  const dispatch = useDispatch();

  // Program types
  const programType = organization?.loyalty.program_type;

  const programsWithRewardsHeader = [
    PROGRAM_TYPES.POINTS_CONVERT_REWARDS,
    PROGRAM_TYPES.SP_POINTS_CONVERT_REWARDS,
  ];

  // For `points_convert_rewards` &`sp_points_convert_rewards` types, we rename the 'Offers' section 'Rewards'
  const header = (
    <StringAccessor
      accessor={
        programsWithRewardsHeader.includes(programType)
          ? 'rewards.my_rewards_header'
          : 'rewards.offers_header'
      }
      html={true}
    />
  );

  return (
    <>
      <StyledRewardsHeaderContainer>
        <StyledRewardsHeader>
          {header} ({myOffers.length})
        </StyledRewardsHeader>
        <StyledBorderBottom />
      </StyledRewardsHeaderContainer>
      <StyledRewardsFlexContainer>
        {myOffers.length > 0 ? (
          myOffers.map((offer, index) => (
            <StyledReward key={index}>
              <StyledRewardTopContent>
                {offer.image?.url && <img src={offer.image.url} alt={offer.label} />}
                <div>
                  <h3>{offer.label}</h3>
                  {offer.expires_at && <p>Expires: {dateFormatter(offer.expires_at)}</p>}
                </div>
              </StyledRewardTopContent>
              <div>
                {offer.description &&
                  offer.description
                    .split('\n')
                    .map((line, i) => (
                      <StyledRewardDescription key={`line-${i}`}>
                        {line.trim()}
                      </StyledRewardDescription>
                    ))}
              </div>
              <StyledCashButtonContainer>
                <OrderNowCTA label="Rewards - Offers" />

                {/* Only show 'Redeem in Store' CTA if supported */}
                <LoyaltyAccessor
                  checkType={LOYALTY_ACCESSOR_TYPES.FEATURE}
                  checkName={LOYALTY_FEATURES.CREATE_REDEMPTION_USING_REWARD}
                  component={
                    <Modal.Container
                      onCancel={() => dispatch(meActions.clearActiveRedemption())}
                      trigger={(open) => (
                        <StyledPrimaryButton
                          data-css-override="StyledPrimaryButton"
                          $buttonTheme="knockout"
                          aria-label={`Click to redeem reward ${offer.label} in store`}
                          onClick={open}
                        >
                          <StringAccessor accessor="rewards.redeem_in_store_button" />
                        </StyledPrimaryButton>
                      )}
                    >
                      {(toggle) => (
                        <ScanCode id={offer.id} type={REWARD_TYPE.OFFER} onError={toggle} />
                      )}
                    </Modal.Container>
                  }
                />
              </StyledCashButtonContainer>

              <Render condition={offer.supports_donation}>
                <StyledButtonContainer>
                  <DonateToSocialCause
                    donation={{
                      type: 'reward',
                      item: offer.id,
                    }}
                    rewardName={offer.label}
                  />
                </StyledButtonContainer>
              </Render>
            </StyledReward>
          ))
        ) : (
          <StyledNoOffers>
            <ConnectedRewardsIcon />
            <StringAccessor tag="h3" accessor="rewards.no_offers_header" html={true} />
            <StringAccessor tag="p" accessor="rewards.no_offers_subheader" html={true} />
          </StyledNoOffers>
        )}
      </StyledRewardsFlexContainer>
    </>
  );
};
